import type { DialogProps } from "@mui/material";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import React, { FC, PropsWithChildren, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { DialogCloseIcon } from "../DialogCloseIcon/DialogCloseIcon";

export interface ConfirmDialogProps extends PropsWithChildren, Omit<DialogProps, "title"> {
  /**
   * Callback to be called when the user confirms the dialog.
   */
  onConfirm: () => void;
  /**
   * Callback to be called when the user cancels the dialog.
   */
  onCancel: () => void;
  /**
   * Title of the dialog.
   */
  title: ReactNode;
  /**
   * Represents the label text for the confirmation button.
   */
  confirmBtnLabel?: string;
  /**
   * Represents the label text for a cancel button in the user interface.
   */
  cancelBtnLabel?: string;
  /**
   * Determines whether the close button in a UI component should be visible.
   * certain modals or dialogs.
   */
  disableCloseButton?: boolean;
  /**
   * A flag indicating whether accidental closing of an application or tab should be disabled. By accidental close
   * is considered press esc key or click out of dialog
   */
  disableAccidentalClose?: boolean;
}

/**
 * Dialog with confirm and cancel buttons. Use for confirmation dialog messages
 */
export const ConfirmDialog: FC<ConfirmDialogProps> = ({
  onConfirm,
  onCancel,
  open,
  title,
  children,
  disableCloseButton,
  confirmBtnLabel,
  cancelBtnLabel,
  disableAccidentalClose,
  ...props
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      onClose={(_, reason) => {
        if (!disableAccidentalClose || (reason !== "escapeKeyDown" && reason !== "backdropClick")) {
          onCancel();
        }
      }}
      open={open}
      {...props}
    >
      <DialogTitle sx={{ minHeight: "80px" }}>
        {title}
        {!disableCloseButton && <DialogCloseIcon onClick={onCancel} />}
      </DialogTitle>
      <DialogContent>{children}</DialogContent>
      <DialogActions>
        <Button onClick={onCancel} size="large">
          {cancelBtnLabel ?? t("cancel")}
        </Button>
        <Button data-cy="confirmDialogConfirmButton" onClick={onConfirm} size="large" variant="contained">
          {confirmBtnLabel ?? t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
